import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { space } from 'styled-system';
import { useSelector, useDispatch } from 'react-redux';
import base64 from 'base-64';
import selectAreaDash from 'redux-api/reselect/area-chart';
import selectRevenue from 'redux-api/reselect/revenue';
import selectDuration from 'redux-api/reselect/duration';
import selectQuantity from 'redux-api/reselect/quantity';
import selectRepartitionCharge from 'redux-api/reselect/repartitionCharge';
import { getCountRequest } from 'redux-api/action/count';
import { getHistogramRequest } from 'redux-api/action/histogram';
import Area from 'theme/charts/area-analytic';
import AreaContentLoader from 'theme/content-loader/area';
import translations from 'translations';
import Card from 'theme/card';

const Wrapper = styled.div`
  ${space}

  width: 100%;
`;

const WrapSign = styled.div`
  position: relative;
`;

function AreaChart({
  compare,
  style,
  dateTo,
  dateFrom,
  dataType,
  dateRange,
  filters,
  terms,
  reloadChecked,
  isAdmin,
}) {
  const [areaQueriedKey, setAreaQueriedKey] = useState(null);
  const [revenueQueriedKey, setRevenueQueriedKey] = useState(null);
  const [chartInterval, setChartInterval] = useState('day');
  const [intervalDiff, setIntervalDiff] = useState(0);
  const [activeKey, setActiveKey] = useState('amounts');

  const dispatch = useDispatch();

  const { formatDate, currency, languageCode } = useSelector((state) => state.config);
  const areaPoints = useSelector((state) => selectAreaDash(state, areaQueriedKey));
  const durationData = useSelector((state) => selectDuration(state, revenueQueriedKey));
  const revenueData = useSelector((state) => selectRevenue(state, revenueQueriedKey));
  const countQuantity = useSelector((state) => selectQuantity(state, revenueQueriedKey));
  const repartitionChargeData = useSelector((state) => selectRepartitionCharge(state, revenueQueriedKey));

  const countData = dataType === 'usage_data' ? durationData : revenueData;

  useEffect(() => {
    const diff = moment(dateTo, formatDate).diff(moment(dateFrom, formatDate), 'days');
    // const interval = diff > 60 ? 'week' : 'day';
    const interval = 'day';
    setIntervalDiff(diff);
    setChartInterval(interval);
  }, [dateTo, dateFrom, formatDate]);

  const filteredAreaPoints = useMemo(() => {
    if (!areaPoints) return null;

    let filteredChart =
      dataType === 'usage_data'
        ? areaPoints.chart.filter((point) => !point.id.match(/revenue/))
        : areaPoints.chart.filter((point) => !point.id.match(/duration/));

    filteredChart = filteredChart.map((point) => {
      if (dataType === 'usage_data' && point.id === 'quantity') {
        return {
          ...point,
          color: 'pie_2_4',
          label: translations[languageCode].users,
        };
      }
      if (dataType === 'usage_data' && point.id === 'duration') {
        return {
          ...point,
          color: 'pie_2_0',
          label: translations[languageCode].hours,
        };
      }
      if (dataType === 'usage_data' && point.id === 'prev_quantity') {
        return {
          ...point,
          color: 'pie_2_5',
          label: translations[languageCode].prevusers,
        };
      }
      if (dataType === 'temporary_data' && point.id === 'revenue') {
        return {
          ...point,
          color: 'primary_300',
        };
      }
      if (dataType === 'temporary_data' && point.id === 'quantity') {
        return {
          ...point,
          color: 'primary_400',
        };
      }
      return point;
    });

    const quantityChart = filteredChart.filter((point) => point.id.match(/quantity/));
    const dataChart = filteredChart.filter((point) => !point.id.match(/quantity/));

    return {
      ...areaPoints,
      chart: dataChart,
      quantityChart,
    };
  }, [areaPoints, dataType, languageCode]);

  const { analytics_type } = useSelector((state) => state.profile.payload);

  const areaQuery = useMemo(() => {
    if (!dataType || !dateTo || !dateFrom) return null;

    let queryString = `interval=${chartInterval}&compare=${compare}&currency=${currency}&dateTo=${dateTo}&dateFrom=${dateFrom}&dateRange=${dateRange}&dataType=${dataType}&version=7&enterprise=${analytics_type || 'standard'
      }&isAdmin=${isAdmin}`;

    if (filters) {
      queryString = `${queryString}&filters=${filters}`;
    }

    if (terms) {
      queryString = `${queryString}&terms=${terms}`;
    }

    return queryString;
  }, [
    dateTo,
    dateFrom,
    dataType,
    dateRange,
    filters,
    terms,
    compare,
    currency,
    analytics_type,
    chartInterval,
    isAdmin,
  ]);

  const revenueQuery = useMemo(() => {
    if (!dataType || !dateTo || !dateFrom) return null;

    let queryString = `currency=${currency}&dataType=${dataType}&dateFrom=${dateFrom}&dateTo=${dateTo}&dateRange=${dateRange}&compare=${compare}&version=7&enterprise=${analytics_type || 'standard'
      }&isAdmin=${isAdmin}`;

    if (filters) {
      queryString = `${queryString}&filters=${filters}`;
    }

    if (terms) {
      queryString = `${queryString}&terms=${terms}`;
    }

    return queryString;
  }, [dataType, dateTo, dateFrom, dateRange, currency, compare, filters, terms, analytics_type, isAdmin]);

  useEffect(() => {
    if (!areaQuery && !revenueQuery) return;

    const areaKey = base64.encode(areaQuery);
    const revenueKey = base64.encode(revenueQuery);

    setAreaQueriedKey(areaKey);
    setRevenueQueriedKey(revenueKey);

    dispatch(getHistogramRequest({ key: areaKey, query: areaQuery, currency }));
    dispatch(getCountRequest({ key: revenueKey, query: revenueQuery, currency }));
  }, [dispatch, areaQuery, revenueQuery, currency, reloadChecked]);

  const handleChangeTab = (key) => {
    setActiveKey(key);
  };

  return (
    <Wrapper {...style}>
      <Card width="100%" border="0px">
        <WrapSign>
          {activeKey === 'amounts' && (
            filteredAreaPoints.chart.length > 1 ? (
              <Area
                chartHeight={360}
                title=''
                chart={filteredAreaPoints.chart}
                isWithinRange={filteredAreaPoints.isWithinRange}
                temporaryRevenue={countData}
                repartitionCharge={repartitionChargeData}
                onChangeInterval={setChartInterval}
                interval={chartInterval}
                intervalDiff={intervalDiff}
                handleChangeTab={handleChangeTab}
                activeKey={activeKey}
                dataType={dataType}
              />
            ) : (
              <AreaContentLoader />
            )
          )}

          {activeKey === 'units' && (
            filteredAreaPoints.quantityChart.length > 1 ? (
              <Area
                chartHeight={360}
                title=''
                chart={filteredAreaPoints.quantityChart}
                isWithinRange={filteredAreaPoints.isWithinRange}
                temporaryRevenue={countQuantity}
                onChangeInterval={setChartInterval}
                interval={chartInterval}
                intervalDiff={intervalDiff}
                handleChangeTab={handleChangeTab}
                activeKey={activeKey}
                dataType={dataType}
              />
            ) : (
              <AreaContentLoader />
            )
          )}
        </WrapSign>
      </Card>
    </Wrapper>
  );
}

export default AreaChart;
